import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { apiService } from 'src/utils/apiServices';
import { PHYSICAL_API, authHeaders, physicalErrorMessage } from 'src/utils/physical-api';

const draftOf = (settings) =>
  Object.fromEntries(settings.map((setting) => [setting.key, String(setting.value ?? '')]));

const changedBy = (setting) => {
  const admin = setting.updated_by_admin;
  if (!admin) return 'Not changed from the dashboard yet';
  const who = `${admin.first_name ?? ''} ${admin.last_name ?? ''}`.trim() || 'an admin';
  return `Changed by ${who} on ${new Date(setting.updated_at).toLocaleString()}`;
};

export default function PhysicalSettingsView() {
  const themeSettings = useSettingsContext();
  const [settings, setSettings] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiService.get(PHYSICAL_API.settings, { headers: authHeaders() });
      setSettings(data ?? []);
      setDraft(draftOf(data ?? []));
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Could not load settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(
    () =>
      settings.reduce((acc, setting) => {
        acc[setting.group] = [...(acc[setting.group] ?? []), setting];
        return acc;
      }, {}),
    [settings]
  );

  const invalidKeys = settings
    .filter((s) => draft[s.key] === '' || !(Number(draft[s.key]) >= s.min))
    .map((s) => s.key);
  const changes = settings.filter(
    (s) => !invalidKeys.includes(s.key) && Number(draft[s.key]) !== Number(s.value)
  );

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiService.patch(
        PHYSICAL_API.settings,
        { settings: changes.map((s) => ({ key: s.key, value: Number(draft[s.key]) })) },
        { headers: authHeaders() }
      );
      setSettings(data);
      setDraft(draftOf(data));
      toast.success('Settings saved!');
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Settings not saved'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth={themeSettings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Settings"
        action={
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              disabled={!changes.length || saving}
              onClick={() => setDraft(draftOf(settings))}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="mdi:content-save-outline" />}
              disabled={!changes.length || invalidKeys.length > 0 || saving}
              onClick={save}
            >
              Save{changes.length ? ` (${changes.length})` : ''}
            </Button>
          </Stack>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Stack spacing={3}>
        {Object.entries(groups).map(([group, items]) => (
          <Card key={group}>
            <CardHeader title={group} />
            <CardContent>
              <Stack spacing={3}>
                {items.map((setting) => {
                  const invalid = invalidKeys.includes(setting.key);
                  return (
                    <Stack
                      key={setting.key}
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      alignItems={{ md: 'flex-start' }}
                    >
                      <TextField
                        type="number"
                        variant="filled"
                        label={setting.label}
                        value={draft[setting.key] ?? ''}
                        onChange={(e) =>
                          setDraft((prev) => ({ ...prev, [setting.key]: e.target.value }))
                        }
                        error={invalid}
                        helperText={
                          invalid
                            ? `At least ${setting.min} ${setting.unit}`
                            : `Default: ${setting.default} ${setting.unit}`
                        }
                        inputProps={{ min: setting.min, step: 'any' }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{setting.unit}</InputAdornment>,
                        }}
                        sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}
                      />
                      <Stack spacing={0.5} sx={{ pt: { md: 1 } }}>
                        <Typography variant="body2">{setting.description}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {changedBy(setting)}
                        </Typography>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Container>
  );
}
